import React, { FC, useCallback, useRef, useState } from "react"

import { Stepper } from "src/components/common/stepper/Stepper"

import { create } from "src/helpers/bem"

import { classnames } from "src/helpers/classnames"
import { TranslationMessages } from "src/translations"

import styles from "./AdvancedForm.module.scss"
import { AdvancedFormFieldProps } from "./AdvancedFormField"
import { AdvancedFormFields } from "./AdvancedFormFields"
import { AdvancedFormStep, AdvancedFormStepProps } from "./AdvancedFormStep"
import { AdvancedFormTitle } from "./AdvancedFormTitle"

const bem = create(styles, "AdvancedForm")
const productNameFields = [
  "client_requestedproducts_standarea",
  "client_requestedproducts_category1",
  "client_requestedproducts_category2",
]
const clientValueFieldNames = [
  "client_requestedareacalculation",
  "client_requestedamount",
]
const requestedStandAreaField = "client_requestedareasize"
const calculatedStandAreaField = "client_calculatedareasize"
const requestedWidthField = "mb_requestedareawidthmin_float"
const requestedDepthField = "mb_requestedareadepthmin_float"

const toFiniteNumber = (
  value: AdvancedFormFieldProps["value"],
): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value
  }
  if (typeof value === "string" && value.trim() !== "") {
    const number = Number(value)
    return Number.isFinite(number) ? number : undefined
  }
  return undefined
}

type StandAreaError = { fieldId: string; errorText: string }

const getMinimumErrorText = (minimum: number, messages?: TranslationMessages) =>
  `${messages?.components.common.fields.inputField.numericMin1} ${minimum} ${messages?.components.common.fields.inputField.numericMin2}`

// Re-evaluates the configured minimum stand area from the current values,
// independent of possibly stale field error texts.
const getStandAreaMinimumError = (
  fields: AdvancedFormFieldProps[],
  messages?: TranslationMessages,
): StandAreaError | undefined => {
  const isActiveAreaField = (field: AdvancedFormFieldProps, name: string) =>
    field.field === name &&
    !!field.id &&
    !!field.required &&
    typeof field.min === "number"

  const directField = fields.find((field) =>
    isActiveAreaField(field, requestedStandAreaField),
  )
  const calculatedField = fields.find((field) =>
    isActiveAreaField(field, calculatedStandAreaField),
  )
  const areaField = directField ?? calculatedField
  if (!areaField?.id || typeof areaField.min !== "number") {
    return undefined
  }

  let area: number | undefined
  if (directField) {
    area = toFiniteNumber(directField.value)
  } else {
    const width = toFiniteNumber(
      fields.find((field) => field.field === requestedWidthField)?.value,
    )
    const depth = toFiniteNumber(
      fields.find((field) => field.field === requestedDepthField)?.value,
    )
    if (width === undefined || depth === undefined) {
      return undefined
    }
    area = width > 0 && depth > 0 ? width * depth : 0
  }

  // Empty or non-numeric values are handled by the existing required/numeric checks.
  if (area === undefined || (area > 0 && area >= areaField.min)) {
    return undefined
  }

  return {
    fieldId: areaField.id,
    errorText: getMinimumErrorText(areaField.min, messages),
  }
}

export interface AdvancedFormCondition {
  [p: string]: boolean
}

export interface AdvancedForms {
  id: string
  name?: string
  label?: string
  description?: string
  position?: number
  helperText?: string
  fields: AdvancedFormFieldProps[]
  conditions?: AdvancedFormCondition
  defaultConditions?: AdvancedFormCondition
}

export type AdvancedFormButtons = {
  backButtonLabel?: string
  nextButtonLabel?: string
  draftButtonLabel?: string
  finishButtonLabel?: string
  saveNewDraftButtonLabel?: string
}

export type AdvancedFormData =
  | ReadonlyArray<AdvancedFormStepProps>
  | ReadonlyArray<AdvancedForms>

export type AdvancedFormProps = {
  activeStep?: number
  id?: string
  className?: string
  stepLabelsClickable?: boolean
  formDisabled?: boolean
  validateMinimumStandArea?: boolean
  fieldsContainerClassName?: string
  data: AdvancedFormData
  messages?: TranslationMessages
  formMessages?: any
  buttons?: AdvancedFormButtons
  handleChangeActiveStep?: (activeStep: number) => void
  handleMergeField: (
    index: number,
    items: AdvancedFormFieldProps,
    name: string | null,
  ) => void
  handleReset?: () => void
  onSendForm?: (status?: "Draft" | "NewDraft") => void
}

export const AdvancedForm: FC<AdvancedFormProps> = ({
  activeStep,
  id,
  className,
  stepLabelsClickable,
  formDisabled,
  validateMinimumStandArea,
  data,
  messages,
  formMessages,
  buttons,
  fieldsContainerClassName,
  handleChangeActiveStep,
  handleMergeField,
  handleReset,
  onSendForm,
}) => {
  const [activeFormsValidAsDraft, setActiveFormsValidAsDraft] =
    useState<boolean>(false)
  const formRef = useRef<HTMLElement>(null)

  const scrollToTop = useCallback(() => {
    formRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    })
  }, [formRef])

  const validateFields = useCallback(async (): Promise<boolean> => {
    let fields
    if (activeStep !== undefined) {
      fields = (data?.[activeStep] as AdvancedFormStepProps)?.forms?.map(
        (form) =>
          form?.fields.map((el) => ({
            ...el,
            value:
              el?.value instanceof Array && el?.value?.length === 0
                ? ""
                : el?.value,
            required:
              form?.conditions?.display === false ||
              el?.conditions?.display === false
                ? false
                : el.required,
            conditions:
              form?.conditions?.display === false
                ? { ...el?.conditions, display: false }
                : el?.conditions,
          })),
      )
    } else {
      fields = (data as AdvancedForms[])?.map((form) => {
        return form?.fields.map((el) => ({
          ...el,
          value:
            el?.value instanceof Array && el?.value?.length === 0
              ? ""
              : el?.value,
          required:
            form?.conditions?.display === false ||
            el?.conditions?.display === false
              ? false
              : el.required,
          conditions:
            form?.conditions?.display === false
              ? { ...el?.conditions, display: false }
              : el?.conditions,
        }))
      })
    }

    const mergedResult = ([] as AdvancedFormFieldProps[]).concat
      .apply([], fields)
      .filter((el) => el?.conditions?.display !== false)

    const standAreaError = validateMinimumStandArea
      ? getStandAreaMinimumError(mergedResult, messages)
      : undefined

    const filteredFieldsForDraft = mergedResult.filter((el) => el?.errorText)
    setActiveFormsValidAsDraft(filteredFieldsForDraft.length > 0)
    const validationFields: AdvancedFormFieldProps[] = standAreaError
      ? mergedResult.map((field) =>
          field.id === standAreaError.fieldId
            ? { ...field, errorText: standAreaError.errorText }
            : field,
        )
      : mergedResult
    const visibleRequiredFields = validationFields
      .filter(
        (el) =>
          (el.required &&
            (el.errorText ||
              el.conditions?.display ||
              el.conditions?.display == undefined)) ||
          (!el.required && el.errorText),
      )
      .map((el) => ({
        ...el,
        conditions: {
          ...el.conditions,
          invalid: (el.required && !el.value) || !!el.errorText,
        },
      }))
    console.log(visibleRequiredFields)
    const formValid = !visibleRequiredFields.some((el) => el.conditions.invalid)
    for (const f of visibleRequiredFields) {
      const field = {
        conditions: f.conditions,
        ...(standAreaError && standAreaError.fieldId === f.id
          ? { errorText: standAreaError.errorText }
          : {}),
      }

      let formIndex = -1
      if (activeStep !== undefined) {
        const currentStep = data[activeStep] as AdvancedFormStepProps
        formIndex =
          currentStep?.forms?.findIndex((form) =>
            form.fields?.some((field) => field.id === f.id),
          ) ?? -1
      } else {
        formIndex = (data as AdvancedForms[]).findIndex((form) =>
          form.fields?.some((field) => field.id === f.id),
        )
      }

      if (formIndex >= 0) {
        updateFormFieldState(
          field,
          f.field as string,
          f.id as string,
          formIndex,
        )
      }
    }

    return formValid
  }, [data, activeStep, messages, validateMinimumStandArea])

  /*useEffect(() => {
    validateFields()
  }, [data, activeStep])*/

  const updateFormFieldState = (
    value: AdvancedFormFieldProps,
    name: string,
    fieldId: string,
    stepIndex: number,
  ) => {
    // Stand calculator
    if (
      (name === requestedDepthField && activeStep !== undefined) ||
      (name === requestedWidthField && activeStep !== undefined)
    ) {
      const totalField = (data[activeStep] as AdvancedFormStepProps)?.forms?.[
        stepIndex
      ]?.fields?.find((el) => el.field === calculatedStandAreaField)
      let complementaryField = requestedWidthField
      if (name === requestedWidthField) {
        complementaryField = requestedDepthField
      }
      const widthOrDepth = (data[activeStep] as AdvancedFormStepProps)?.forms?.[
        stepIndex
      ]?.fields?.find((el) => el.field === complementaryField)?.value
      let totalValue = Number(value?.value) * Number(widthOrDepth)
      let errorText
      if (!!totalField?.min && totalValue < totalField?.min) {
        errorText = getMinimumErrorText(totalField.min, messages)
      }
      if (!!totalField?.max && totalValue > totalField?.max) {
        errorText = `${messages?.components.common.fields.inputField.numericMax1} ${totalField.max} ${messages?.components.common.fields.inputField.numericMax2}`
      }
      if (!isNaN(totalValue)) {
        handleMergeField(
          stepIndex,
          {
            value: totalValue.toString(),
            errorText,
          },
          totalField?.id ?? totalField?.name ?? "",
        )
      }
    }
    return handleMergeField(stepIndex, value, fieldId)
  }

  const updateFieldState = (value: AdvancedFormFieldProps, name?: string) => {
    const Name = name?.split("/")
    if (Name) {
      let formIndex
      if (activeStep !== undefined) {
        formIndex = (
          data[activeStep] as AdvancedFormStepProps
        )?.forms.findIndex((el) => el.id === Name[0])
      } else {
        formIndex = (data as AdvancedForms[]).findIndex(
          (el) => el.id === Name[0],
        )
      }

      let fieldNameIsProduct = productNameFields.includes(Name[2])
      if (fieldNameIsProduct && activeStep !== undefined) {
        const _formIndex = formIndex + 1
        clientValueFieldNames.forEach((fieldName, fieldNameIndex) => {
          const valueFieldNameIndex = _formIndex + fieldNameIndex
          const fieldToClear = (
            data[activeStep] as AdvancedFormStepProps
          )?.forms?.[valueFieldNameIndex]?.fields?.find(
            (el) => el.field === fieldName,
          )
          if (fieldToClear !== undefined) {
            handleMergeField(
              valueFieldNameIndex,
              {
                value: "",
                errorText: undefined,
              },
              fieldToClear?.id ?? fieldToClear?.name ?? "",
            )
          }
        })
      }

      updateFormFieldState(value, Name[2], Name[1], formIndex)
    }
  }

  const handleBackStep = () => {
    handleChangeActiveStep?.((activeStep ?? 0) - 1)
  }

  const handleNextStep = async () => {
    const formValid = await validateFields()
    if (!formValid) {
      scrollToTop()
      return
    }

    if (data && data?.length - 1 === activeStep) {
      onSendForm?.()
      handleReset?.()
      return
    }
    handleChangeActiveStep?.((activeStep ?? 0) + 1)
    scrollToTop()
  }

  const handleSaveDraft = async () => {
    onSendForm?.("Draft")
    return
  }

  const handleSaveNewDraft = async () => {
    onSendForm?.("NewDraft")
    return
  }

  if (activeStep === undefined) {
    return (
      <>
        {(data as AdvancedForms[])?.map((form) => (
          <div key={form.id} className={bem("form")}>
            <AdvancedFormTitle
              activeStep={activeStep}
              data={data}
              form={form}
              formMessages={formMessages}
            />
            <AdvancedFormFields
              activeStep={activeStep}
              containerClassName={fieldsContainerClassName}
              data={data}
              fields={form?.fields}
              formDisabled={formDisabled}
              formId={form?.id}
              formMessages={formMessages}
              id={id}
              messages={messages}
              updateFieldState={updateFieldState}
            />
          </div>
        ))}
      </>
    )
  }

  let ButtonsRight = [
    {
      label:
        activeStep === data?.length - 1
          ? (buttons?.finishButtonLabel ?? "")
          : (buttons?.nextButtonLabel ?? ""),
      onClick: handleNextStep,
    },
  ]
  if (buttons?.saveNewDraftButtonLabel) {
    const arr = [
      {
        label: buttons?.saveNewDraftButtonLabel ?? "",
        disabled: activeFormsValidAsDraft,
        onClick: handleSaveNewDraft,
      },
    ]

    if (buttons?.draftButtonLabel) {
      arr.push({
        label: buttons?.draftButtonLabel ?? "",
        disabled: activeFormsValidAsDraft,
        onClick: handleSaveDraft,
      })
    }
    ButtonsRight = [...arr, ...ButtonsRight]
  }

  return (
    <Stepper
      ref={formRef}
      buttonsRight={ButtonsRight}
      className={classnames(bem(), className)}
      index={activeStep ?? 0}
      buttonsLeft={[
        {
          label: buttons?.backButtonLabel ?? "",
          disabled: activeStep === 0,
          onClick: handleBackStep,
        },
      ]}
      steps={
        data?.map((el, i) => ({
          title: {
            text:
              el?.name ??
              el?.label ??
              (formMessages as any)?.[el?.id]?.title ??
              "",
            onClick:
              stepLabelsClickable && i < activeStep
                ? () => handleChangeActiveStep?.(i)
                : undefined,
          },
        })) ?? []
      }
    >
      <AdvancedFormStep
        activeStep={activeStep}
        data={data}
        formDisabled={formDisabled}
        formMessages={formMessages}
        id={id}
        messages={messages}
        updateFieldState={updateFieldState}
      />
    </Stepper>
  )
}
